import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
	DollarSign,
	PiggyBank,
	TrendingDown,
	Calendar,
	Home,
	TrendingUp,
	BarChart3,
	Clock,
} from "lucide-react";
import { Skeleton, Carousel, useIsMobile, formatCurrency as formatCurrencyUI } from "@hotelise/ui";

import {
	getDashboardReportData,
	getDashboardReportLoading,
} from "state/modules/dashboard";

interface SummaryCard {
	title: string;
	value: string;
	breakdown?: string;
	icon: React.ElementType;
}

const SKELETON_CARDS = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j"];

const SkeletonCard = () => (
	<div className="bg-card rounded-lg p-4 border">
		<div className="flex items-center justify-between mb-2">
			<Skeleton className="h-4 w-24" />
			<Skeleton className="h-4 w-4 rounded" />
		</div>
		<div className="space-y-1">
			<Skeleton className="h-8 w-28" />
			<Skeleton className="h-3 w-36" />
		</div>
	</div>
);

const MetricCard = ({
	metric,
	className,
}: {
	metric: SummaryCard;
	className?: string;
}) => {
	const Icon = metric.icon;
	return (
		<div
			className={`bg-card rounded-lg p-4 border hover:shadow-sm transition-shadow ${className || ""}`}
		>
			<div className="flex items-center justify-between mb-2">
				<h3 className="text-sm font-medium text-muted-foreground">
					{metric.title}
				</h3>
				<Icon className="h-4 w-4 text-muted-foreground" />
			</div>
			<div className="space-y-1">
				<p className="text-2xl font-bold">{metric.value}</p>
				{metric.breakdown && (
					<p className="text-xs text-muted-foreground">{metric.breakdown}</p>
				)}
			</div>
		</div>
	);
};

const DashboardSummary = () => {
	const report = useSelector(getDashboardReportData);
	const isLoading = useSelector(getDashboardReportLoading);
	const isMobile = useIsMobile();

	const summaryItems: SummaryCard[] = useMemo(() => {
		if (!report) return [];

		const calc = report.calculations;
		if (!calc) return [];

		const fmtCurrency = (v: number | undefined) =>
			formatCurrencyUI(Math.round(v || 0));

		const buildBreakdown = (
			amd: number | undefined,
			usd: number | undefined
		) => {
			if (amd == null && usd == null) return undefined;
			const parts: string[] = [];
			if (amd != null) parts.push(formatCurrencyUI(Math.round(amd), false, 1, "֏"));
			if (usd != null) parts.push(formatCurrencyUI(Math.round(usd)));
			return parts.join(" • ");
		};

		return [
			{
				title: "Owner Payouts",
				value: fmtCurrency(calc.totalInCurrency?.totalIncomeInCurrency),
				breakdown: buildBreakdown(
					calc.totalIncomePerCurrency?.AMD,
					calc.totalIncomePerCurrency?.USD
				),
				icon: DollarSign,
			},
			{
				title: "Owner Net Income",
				value: fmtCurrency(
					calc.totalInCurrency?.netOwnerIncomeInCurrency
				),
				icon: PiggyBank,
			},
			{
				title: "Expenses",
				value: fmtCurrency(calc.totalInCurrency?.totalExpensesInCurrency),
				breakdown: buildBreakdown(
					calc.totalExpensesPerCurrency?.AMD,
					calc.totalExpensesPerCurrency?.USD
				),
				icon: TrendingDown,
			},
			{
				title: "Reservations",
				value: `${calc.reservationPeriod?.numberOfReservations || 0}`,
				icon: Calendar,
			},
			{
				title: "Cleaning",
				value: fmtCurrency(calc.totalInCurrency?.totalCleaningInCurrency),
				breakdown: buildBreakdown(
					calc.totalCleaningPerCurrency?.AMD,
					calc.totalCleaningPerCurrency?.USD
				),
				icon: Home,
			},
			{
				title: "Occupancy Rate",
				value: `${(calc.occupancyRate || 0).toFixed(1)}%`,
				icon: TrendingUp,
			},
			{
				title: "ADR",
				value: fmtCurrency(calc.totalInCurrency?.adr),
				icon: BarChart3,
			},
			{
				title: "RevPAR",
				value: fmtCurrency(calc.totalInCurrency?.revPar),
				icon: DollarSign,
			},
			{
				title: "ALOS",
				value: `${(calc.alos || 0).toFixed(1)} days`,
				icon: Clock,
			},
			{
				title: "OOO",
				value: `${calc.reservationPeriod?.ownerBlockedDays || 0} days`,
				breakdown: `Est. lost income: ${fmtCurrency((calc.reservationPeriod?.ownerBlockedDays || 0) * (calc.totalInCurrency?.adr || 0))}`,
				icon: Calendar,
			},
		];
	}, [report]);

	if (isLoading) {
		if (isMobile) {
			return (
				<Carousel showDots={false}>
					{SKELETON_CARDS.slice(0, 3).map((id) => (
						<SkeletonCard key={id} />
					))}
				</Carousel>
			);
		}

		return (
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
				{SKELETON_CARDS.map((id) => (
					<SkeletonCard key={id} />
				))}
			</div>
		);
	}

	if (isMobile) {
		return (
			<Carousel slideClassName="flex-[0_0_70%] min-w-0">
				{summaryItems.map((metric) => (
					<MetricCard
						key={metric.title}
						metric={metric}
						className="h-full"
					/>
				))}
			</Carousel>
		);
	}

	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
			{summaryItems.map((metric) => (
				<MetricCard key={metric.title} metric={metric} />
			))}
		</div>
	);
};

export default DashboardSummary;
